import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Phone,
  Envelope,
  Globe,
  Trash,
  PencilSimple,
  Lightning,
  Export,
  FunnelSimple,
  ArrowRight,
} from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: "new", label: "New" },
  { value: "called", label: "Called" },
  { value: "pitched", label: "Pitched" },
  { value: "won", label: "Won ($500)" },
  { value: "lost", label: "Lost" },
  { value: "no_answer", label: "No Answer" },
];

export default function LeadManager() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editLead, setEditLead] = useState(null);
  const [editNotes, setEditNotes] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const navigate = useNavigate();

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? `?status=${filter}` : "";
      const res = await axios.get(`${API}/leads${params}`);
      setLeads(res.data.leads || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadLeads();
  }, [loadLeads]);

  const updateStatus = async (leadId, newStatus) => {
    try {
      await axios.patch(`${API}/leads/${leadId}`, { status: newStatus });
      setLeads((prev) =>
        prev.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l))
      );
      toast.success(`Status updated to ${newStatus}`);
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const deleteLead = async (leadId) => {
    try {
      await axios.delete(`${API}/leads/${leadId}`);
      setLeads((prev) => prev.filter((l) => l.id !== leadId));
      toast.success("Lead deleted");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const saveEdit = async () => {
    if (!editLead) return;
    try {
      await axios.patch(`${API}/leads/${editLead.id}`, {
        notes: editNotes,
        status: editStatus,
      });
      setLeads((prev) =>
        prev.map((l) =>
          l.id === editLead.id ? { ...l, notes: editNotes, status: editStatus } : l
        )
      );
      setEditLead(null);
      toast.success("Lead updated");
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const extractPhone = async (leadId) => {
    try {
      const res = await axios.post(`${API}/leads/${leadId}/extract-phone`);
      setLeads((prev) =>
        prev.map((l) => (l.id === leadId ? res.data : l))
      );
      toast.success("Phone extraction complete");
    } catch (e) {
      toast.error("Phone extraction failed");
    }
  };

  const handleExport = () => {
    const url = filter !== "all"
      ? `${API}/leads/export/csv?status=${filter}`
      : `${API}/leads/export/csv`;
    window.open(url, "_blank");
  };

  return (
    <div data-testid="lead-manager-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1
            className="text-3xl sm:text-4xl font-black tracking-tighter"
            style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}
          >
            Lead Manager
          </h1>
          <p className="text-sm text-[#52525B] mt-1">
            {total} leads total. Track status, manage pipeline, export for cold calling.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 border border-border px-3 py-1.5 bg-white">
            <FunnelSimple weight="bold" size={14} className="text-[#52525B]" />
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="lead-filter" className="border-none shadow-none h-auto p-0 text-xs font-semibold w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <button
            data-testid="export-csv-btn"
            onClick={handleExport}
            className="flex items-center gap-1 px-3 py-2 border border-border text-xs font-semibold hover:border-[#002FA7] hover:text-[#002FA7] transition-colors bg-white"
          >
            <Export weight="bold" size={14} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Leads Table */}
      <div className="bg-white border border-border">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="spinner" />
          </div>
        ) : leads.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32 text-[#A1A1AA]">
            <p className="text-sm">No leads found</p>
            <button
              onClick={() => navigate("/scanner")}
              className="text-xs text-[#002FA7] font-semibold mt-2 hover:underline"
            >
              Start scanning
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full dense-table">
              <thead>
                <tr>
                  <th className="text-left">Business</th>
                  <th className="text-left">Phone</th>
                  <th className="text-left">Email</th>
                  <th className="text-center">Score</th>
                  <th className="text-left">Status</th>
                  <th className="text-left">Notes</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead, i) => (
                  <tr key={lead.id} className="animate-fade-in-up" style={{ animationDelay: `${i * 0.03}s` }}>
                    <td className="font-medium max-w-[180px]">
                      <div className="truncate">{lead.business_name || "—"}</div>
                      {lead.url && (
                        <a
                          href={lead.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] text-[#002FA7] font-mono hover:underline"
                        >
                          {lead.domain || lead.url}
                        </a>
                      )}
                    </td>
                    <td>
                      {lead.phones?.length > 0 ? (
                        <div className="space-y-0.5">
                          {lead.phones.slice(0, 2).map((p, j) => (
                            <a key={j} href={`tel:${p}`} className="flex items-center gap-1 text-xs font-mono text-[#0A0A0A] hover:text-[#002FA7]">
                              <Phone weight="bold" size={10} className="text-[#00C853]" />
                              {p}
                            </a>
                          ))}
                        </div>
                      ) : (
                        <button
                          data-testid={`extract-phone-${i}`}
                          onClick={() => extractPhone(lead.id)}
                          className="text-[10px] text-[#002FA7] font-semibold hover:underline"
                        >
                          Extract
                        </button>
                      )}
                    </td>
                    <td className="text-xs max-w-[140px] truncate">
                      {lead.emails?.[0] ? (
                        <a href={`mailto:${lead.emails[0]}`} className="flex items-center gap-1 hover:text-[#002FA7]">
                          <Envelope weight="bold" size={10} />
                          {lead.emails[0]}
                        </a>
                      ) : "—"}
                    </td>
                    <td className="text-center">
                      <span className={`inline-block px-2 py-0.5 text-xs font-bold grade-${(lead.quality_grade || "f").toLowerCase()}`}>
                        {lead.quality_grade || "?"} ({lead.quality_score || 0})
                      </span>
                    </td>
                    <td>
                      <Select
                        value={lead.status || "new"}
                        onValueChange={(val) => updateStatus(lead.id, val)}
                      >
                        <SelectTrigger data-testid={`status-select-${i}`} className={`h-7 text-xs font-semibold border-none shadow-none px-2 py-0.5 status-${lead.status || "new"} w-[95px]`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((s) => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="text-xs max-w-[120px] truncate text-[#52525B]">
                      {lead.notes || "—"}
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          data-testid={`edit-lead-${i}`}
                          onClick={() => {
                            setEditLead(lead);
                            setEditNotes(lead.notes || "");
                            setEditStatus(lead.status || "new");
                          }}
                          className="p-1.5 hover:bg-[#F5F5F7] transition-colors"
                          title="Edit"
                        >
                          <PencilSimple size={14} />
                        </button>
                        <button
                          data-testid={`build-site-lead-${i}`}
                          onClick={() =>
                            navigate(
                              `/generator?leadId=${lead.id}&name=${encodeURIComponent(lead.business_name || "")}&industry=${encodeURIComponent(lead.industry || "")}`
                            )
                          }
                          className="p-1.5 hover:bg-[#EEF2FF] text-[#002FA7] transition-colors"
                          title="Build website"
                        >
                          <Lightning weight="bold" size={14} />
                        </button>
                        <button
                          data-testid={`delete-lead-${i}`}
                          onClick={() => deleteLead(lead.id)}
                          className="p-1.5 hover:bg-[#FEE2E2] text-[#FF2A00] transition-colors"
                          title="Delete"
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={!!editLead} onOpenChange={() => setEditLead(null)}>
        <DialogContent className="rounded-none border border-border">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
              Edit Lead: {editLead?.business_name}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#52525B]">
              Update status and add call notes for this lead.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] block mb-1">Status</label>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger data-testid="edit-status-select" className="rounded-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.15em] text-[#52525B] block mb-1">Notes</label>
              <Textarea
                data-testid="edit-notes-textarea"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                rows={4}
                placeholder="Call notes, meeting details..."
                className="rounded-none border-border focus:border-[#002FA7] focus:ring-0"
              />
            </div>
            <button
              data-testid="save-edit-btn"
              onClick={saveEdit}
              className="w-full px-4 py-2.5 bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#002482] transition-colors"
            >
              Save Changes
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
